import React, { useState } from "react";
import { X, Dumbbell, Apple, Scale, Droplets, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toKg, weightUnit } from "../../lib/units";

const TITLES = { workout: "Log a workout", nutrition: "Log nutrition", weight: "Log weight" };

export function QuickLogModal({ mode, onClose, onDone }) {
  const { member } = useAuth();
  const [busy, setBusy] = useState(false);
  const units = member?.preferredUnits || "metric";

  // workout
  const [w, setW] = useState({ exercise: "", sets: "3", reps: "10", weight: "" });
  // nutrition
  const [meal, setMeal] = useState({ name: "", calories: "", protein: "" });
  // weight
  const [kgVal, setKgVal] = useState("");

  const finish = (msg, desc) => { toast.success(msg, { description: desc }); onDone && onDone(); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "workout") {
        if (!w.exercise.trim()) throw new Error("Enter an exercise");
        await api.addSet({ exercise: w.exercise.trim(), sets: parseInt(w.sets) || 1, reps: parseInt(w.reps) || 1, weight: parseFloat(w.weight) || 0 });
        finish("Workout logged 💪", `${w.exercise} · ${w.sets}×${w.reps}`);
      } else if (mode === "nutrition") {
        if (!meal.name.trim()) throw new Error("Enter a meal name");
        await api.addMeal({ name: meal.name.trim(), cal: parseInt(meal.calories) || 0, p: parseInt(meal.protein) || 0, carbs: 0, fat: 0 });
        finish("Meal logged 🍽️", meal.name);
      } else if (mode === "weight") {
        const entered = parseFloat(kgVal);
        if (!entered) throw new Error("Enter your weight");
        await api.addWeight(toKg(entered, units));
        finish("Weight logged", `${entered} ${weightUnit(units)}`);
      }
    } catch (err) { toast.error(err.message || "Couldn't save"); }
    finally { setBusy(false); }
  };

  const addWater = async (ml) => {
    setBusy(true);
    try { await api.addWater(ml); finish("Water logged 💧", `+${ml}ml`); }
    catch (err) { toast.error("Couldn't save"); }
    finally { setBusy(false); }
  };

  const Icon = mode === "workout" ? Dumbbell : mode === "nutrition" ? Apple : Scale;

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/70 p-0 backdrop-blur sm:items-center sm:p-4" onClick={onClose} data-testid="quick-log-modal">
      <div className="w-full max-w-md rounded-t-3xl border-t border-white/10 bg-ink-surface p-6 sm:rounded-3xl sm:border" onClick={(e) => e.stopPropagation()} style={{ animation: "aeva-rise 0.28s cubic-bezier(0.22,1,0.36,1) both" }}>
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5"><span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand/15 text-brand"><Icon size={18} /></span><h3 className="text-lg font-semibold text-mist">{TITLES[mode]}</h3></div>
          <button onClick={onClose} data-testid="quick-log-close" className="text-mist-muted hover:text-mist"><X size={22} /></button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "workout" && (
            <>
              <input autoFocus value={w.exercise} onChange={(e) => setW({ ...w, exercise: e.target.value })} data-testid="ql-exercise" placeholder="Exercise (e.g. Bench press)" className="aeva-input w-full" />
              <div className="grid grid-cols-3 gap-2">
                <label className="text-xs text-mist-muted">Sets<input value={w.sets} onChange={(e) => setW({ ...w, sets: e.target.value })} data-testid="ql-sets" type="number" className="aeva-input mt-1 w-full" /></label>
                <label className="text-xs text-mist-muted">Reps<input value={w.reps} onChange={(e) => setW({ ...w, reps: e.target.value })} data-testid="ql-reps" type="number" className="aeva-input mt-1 w-full" /></label>
                <label className="text-xs text-mist-muted">{weightUnit(units)}<input value={w.weight} onChange={(e) => setW({ ...w, weight: e.target.value })} data-testid="ql-weight" type="number" className="aeva-input mt-1 w-full" /></label>
              </div>
            </>
          )}
          {mode === "nutrition" && (
            <>
              <input autoFocus value={meal.name} onChange={(e) => setMeal({ ...meal, name: e.target.value })} data-testid="ql-meal" placeholder="Meal (e.g. Chicken & rice)" className="aeva-input w-full" />
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs text-mist-muted">Calories<input value={meal.calories} onChange={(e) => setMeal({ ...meal, calories: e.target.value })} data-testid="ql-calories" type="number" className="aeva-input mt-1 w-full" /></label>
                <label className="text-xs text-mist-muted">Protein (g)<input value={meal.protein} onChange={(e) => setMeal({ ...meal, protein: e.target.value })} data-testid="ql-protein" type="number" className="aeva-input mt-1 w-full" /></label>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <span className="text-xs text-mist-muted">Or quick water:</span>
                {[250, 500, 750].map((ml) => (
                  <button key={ml} type="button" onClick={() => addWater(ml)} disabled={busy} className="btn-ghost flex items-center gap-1 !py-1.5 text-xs"><Droplets size={12} /> {ml}ml</button>
                ))}
              </div>
            </>
          )}
          {mode === "weight" && (
            <label className="block text-xs text-mist-muted">Weight ({weightUnit(units)})
              <input autoFocus value={kgVal} onChange={(e) => setKgVal(e.target.value)} data-testid="ql-weight-value" type="number" step="0.1" placeholder={units === "imperial" ? "150" : "68.2"} className="aeva-input mt-1 w-full" />
            </label>
          )}
          <button type="submit" disabled={busy} data-testid="quick-log-submit" className="btn-primary mt-2 flex w-full items-center justify-center gap-2">
            {busy && <Loader2 size={16} className="animate-spin" />} Save
          </button>
        </form>
      </div>
    </div>
  );
}
